const DEFAULT_DIMENSIONS = { alto: '1', ancho: '1', profundidad: '1', peso: '1' };
const DEFAULT_ZONAS = { amba: true, interior: true, patagonia: true };

const normalizeNumericString = (value) => {
    const rawValue = String(value ?? '').trim();
    if (rawValue === '') return '';

    const compactValue = rawValue.replace(/\s+/g, '');

    if (/^-?\d{1,3}(\.\d{3})*,\d+$/.test(compactValue)) {
        return compactValue.replace(/\./g, '').replace(',', '.');
    }

    if (/^-?\d{1,3}(,\d{3})*\.\d+$/.test(compactValue)) {
        return compactValue.replace(/,/g, '');
    }

    if (/^-?\d+,\d+$/.test(compactValue)) {
        return compactValue.replace(',', '.');
    }

    return compactValue.replace(',', '.');
};

export const toCreacionMasivaNumber = (value, fallback = 0) => {
    if (value == null || String(value).trim() === '') return fallback;

    const numericValue = Number(normalizeNumericString(value));
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeImportedNumericValue = (value, fallback = '') => {
    const rawValue = String(value ?? '').trim();
    if (rawValue === '') return fallback;

    if (/^-?\d{1,3}(,\d{3})+$/.test(rawValue)) {
        return rawValue.replace(/,/g, '');
    }

    if (/^-?\d{1,3}(\.\d{3})+$/.test(rawValue)) {
        return rawValue.replace(/\./g, '');
    }

    return normalizeNumericString(rawValue);
};

const normalizeHeader = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_.-]+/g, '');

const getFirstRecord = (rawValue) => {
    let record = '';
    let inQuotes = false;

    for (let index = 0; index < rawValue.length; index += 1) {
        const currentChar = rawValue[index];
        const nextChar = rawValue[index + 1];

        if (currentChar === '"') {
            if (inQuotes && nextChar === '"') {
                record += currentChar;
                index += 1;
            } else {
                inQuotes = !inQuotes;
                record += currentChar;
            }
            continue;
        }

        if ((currentChar === '\n' || currentChar === '\r') && !inQuotes) {
            break;
        }

        record += currentChar;
    }

    return record;
};

export const detectCreacionMasivaDelimiter = (rawValue) => {
    const firstRecord = getFirstRecord(rawValue);
    const candidates = ['\t', ';', ','];
    const selected = candidates
        .map((candidate) => ({
            candidate,
            count: firstRecord.split(candidate).length - 1
        }))
        .sort((left, right) => right.count - left.count)[0];

    return selected?.count > 0 ? selected.candidate : ';';
};

export const parseCreacionMasivaDelimitedText = (rawValue, delimiter) => {
    const rows = [];
    let currentRow = [];
    let currentField = '';
    let inQuotes = false;

    const pushField = () => {
        currentRow.push(currentField.trim());
        currentField = '';
    };

    const pushRow = () => {
        if (currentRow.some((field) => field !== '')) {
            rows.push(currentRow);
        }
        currentRow = [];
    };

    for (let index = 0; index < rawValue.length; index += 1) {
        const currentChar = rawValue[index];
        const nextChar = rawValue[index + 1];

        if (currentChar === '"') {
            if (inQuotes && nextChar === '"') {
                currentField += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (currentChar === delimiter && !inQuotes) {
            pushField();
            continue;
        }

        if ((currentChar === '\n' || currentChar === '\r') && !inQuotes) {
            if (currentChar === '\r' && nextChar === '\n') {
                index += 1;
            }
            pushField();
            pushRow();
            continue;
        }

        currentField += currentChar;
    }

    pushField();
    pushRow();

    return rows;
};

const parseBooleanLike = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    return ['1', 'si', 'sí', 'true', 'x', 'yes', 'y'].includes(normalized);
};

const parseImageUrls = (rawValue) => String(rawValue ?? '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter((item) => item !== '');

const createInitialDefaults = (fallbackSellerId) => ({
    categoria: '',
    marca: '',
    idTipoPublicacion: '',
    alicuotaIva: '',
    alicuotaImpuestoInterno: '0',
    observacion: '',
    descripcionAmpliada: '',
    tipoEntrega: '1',
    envioGratis: true,
    envioPropio: false,
    garantiaExtActiva: false,
    garantiaFabrica: '0',
    replicateDimensions: true,
    alto: '1',
    ancho: '1',
    profundidad: '1',
    peso: '1',
    selectedSellerId: fallbackSellerId ?? ''
});

const buildRowImages = (row) => parseImageUrls(row.imagenesTexto)
    .slice(0, 5)
    .map((urlImagen, index) => ({
        posicion: index + 1,
        urlImagen
    }));

const buildMasivaBulkItem = (row, defaults, selectedSellerId) => ({
    idSeller: toCreacionMasivaNumber(selectedSellerId, 0),
    observacion: defaults.observacion,
    titulo: String(row.titulo ?? '').trim(),
    ean: String(row.ean ?? '').trim(),
    precioLista: toCreacionMasivaNumber(row.precioLista, 0),
    precioPromocional: row.precioPromocional === '' ? 0 : toCreacionMasivaNumber(row.precioPromocional, 0),
    linkYoutube: String(row.linkYoutube ?? '').trim(),
    descripcionAmpliada: String(row.descripcionAmpliada || defaults.descripcionAmpliada || '').trim(),
    tipoEntrega: toCreacionMasivaNumber(defaults.tipoEntrega, 1),
    envioGratis: !!(defaults.envioPropio
        ? Object.values(row.zonasEnvioGratis ?? {}).some(Boolean)
        : defaults.envioGratis),
    dimensiones: [
        {
            alto: toCreacionMasivaNumber(defaults.replicateDimensions ? defaults.alto : row.dimensiones?.alto, 1),
            ancho: toCreacionMasivaNumber(defaults.replicateDimensions ? defaults.ancho : row.dimensiones?.ancho, 1),
            profundidad: toCreacionMasivaNumber(defaults.replicateDimensions ? defaults.profundidad : row.dimensiones?.profundidad, 1),
            peso: toCreacionMasivaNumber(defaults.replicateDimensions ? defaults.peso : row.dimensiones?.peso, 1)
        }
    ],
    categoria: toCreacionMasivaNumber(defaults.categoria, 0),
    marca: toCreacionMasivaNumber(defaults.marca, 0),
    skuSeller: String(row.skuSeller ?? '').trim(),
    stock: toCreacionMasivaNumber(row.stock, 0),
    imagenes: buildRowImages(row),
    idTipoPublicacion: toCreacionMasivaNumber(defaults.idTipoPublicacion, 0),
    envioPropio: !!defaults.envioPropio,
    garantiaExtActiva: !!defaults.garantiaExtActiva,
    garantiaFabrica: toCreacionMasivaNumber(defaults.garantiaFabrica, 0),
    codigoLegibilidad: String(row.codigoLegibilidad ?? '').trim(),
    idMoneda: 0,
    alicuotaIva: toCreacionMasivaNumber(defaults.alicuotaIva, 0),
    alicuotaImpuestoInterno: toCreacionMasivaNumber(defaults.alicuotaImpuestoInterno, 0),
    costoFeeAmba: toCreacionMasivaNumber(row.costosFee?.amba, 0),
    costoFeeInterior: toCreacionMasivaNumber(row.costosFee?.interior, 0),
    costoFeePatagonia: toCreacionMasivaNumber(row.costosFee?.patagonia, 0),
    envioGratisZona: {
        amba: !!(defaults.envioPropio ? row.zonasEnvioGratis?.amba : defaults.envioGratis && row.zonasEnvioGratis?.amba),
        interior: !!(defaults.envioPropio ? row.zonasEnvioGratis?.interior : defaults.envioGratis && row.zonasEnvioGratis?.interior),
        patagonia: !!(defaults.envioPropio ? row.zonasEnvioGratis?.patagonia : defaults.envioGratis && row.zonasEnvioGratis?.patagonia)
    }
});

export const buildMasivaBulkRequestFromRecords = (records, {
    userId,
    currentSellerId,
    isPrivilegedUser = false
} = {}) => {
    if (!Array.isArray(records) || records.length === 0) {
        return { masivaBulks: [], idUser: userId ?? 0 };
    }

    const firstValues = records[0];
    const normalizedHeaders = firstValues.map(normalizeHeader);
    const hasHeader = normalizedHeaders.some((header) => [
        'idseller', 'skuseller', 'sku', 'titulo', 'preciolista', 'precio', 'imagen1', 'img1', 'feeamba', 'costofeeamba'
    ].includes(header));

    const headerLookup = normalizedHeaders.reduce((accumulator, header, index) => {
        accumulator[header] = [...(accumulator[header] ?? []), index];
        return accumulator;
    }, {});

    const getValuesByAliases = (values, aliases) => aliases.flatMap((alias) => {
        const indexes = headerLookup[alias] ?? [];
        return indexes.map((index) => values[index] ?? '');
    });

    const getFirstValueByAliases = (values, aliases, fallback = '') => {
        const candidate = getValuesByAliases(values, aliases)
            .map((item) => String(item ?? '').trim())
            .find((item) => item !== '');
        return candidate ?? fallback;
    };

    const getFirstValueFromRecords = (aliases, fallback = '') => {
        for (const record of records.slice(hasHeader ? 1 : 0)) {
            const value = getFirstValueByAliases(record, aliases, '');
            if (value !== '') return value;
        }

        return fallback;
    };

    const hasAnyHeader = (aliases) => aliases.some((alias) => (headerLookup[alias] ?? []).length > 0);
    const dataRecords = hasHeader ? records.slice(1) : records;
    const importedRows = [];

    dataRecords.forEach((values) => {
        if (!Array.isArray(values) || values.every((value) => String(value ?? '').trim() === '')) return;

        const row = {
            skuSeller: '',
            titulo: '',
            ean: '',
            descripcionAmpliada: '',
            precioLista: '',
            precioPromocional: '',
            stock: '0',
            codigoLegibilidad: '',
            linkYoutube: '',
            imagenesTexto: '',
            costosFee: { amba: '', interior: '', patagonia: '' },
            zonasEnvioGratis: { ...DEFAULT_ZONAS },
            dimensiones: { ...DEFAULT_DIMENSIONS }
        };

        if (hasHeader) {
            const imageHeaders = [
                ['imagen1', 'img1'],
                ['imagen2', 'img2'],
                ['imagen3', 'img3'],
                ['imagen4', 'img4'],
                ['imagen5', 'img5']
            ];
            const imageUrls = imageHeaders
                .map((aliases) => getFirstValueByAliases(values, aliases, ''))
                .filter((item) => item !== '')
                .slice(0, 5);

            row.skuSeller = getFirstValueByAliases(values, ['skuseller', 'sku']);
            row.titulo = getFirstValueByAliases(values, ['titulo', 'nombre', 'title']);
            row.ean = getFirstValueByAliases(values, ['ean']);
            row.descripcionAmpliada = getFirstValueByAliases(values, ['descripcionampliada', 'descripcionlarga', 'descripcion']);
            row.precioLista = normalizeImportedNumericValue(getFirstValueByAliases(values, ['preciolista', 'precio']));
            row.precioPromocional = normalizeImportedNumericValue(getFirstValueByAliases(values, ['preciopromocional', 'preciopromo', 'promocional']));
            row.stock = normalizeImportedNumericValue(getFirstValueByAliases(values, ['stock'], '0'), '0');
            row.codigoLegibilidad = getFirstValueByAliases(values, ['codigolegibilidad', 'legibilidad']);
            row.linkYoutube = getFirstValueByAliases(values, ['linkyoutube', 'youtube', 'video']);
            row.imagenesTexto = imageUrls.join('\n');
            row.costosFee = {
                amba: normalizeImportedNumericValue(getFirstValueByAliases(values, ['costofeeamba', 'feeamba', 'costoamba'])),
                interior: normalizeImportedNumericValue(getFirstValueByAliases(values, ['costofeeinterior', 'feeinterior', 'costointerior'])),
                patagonia: normalizeImportedNumericValue(getFirstValueByAliases(values, ['costofeepatagonia', 'feepatagonia', 'costopatagonia']))
            };
            row.zonasEnvioGratis = {
                amba: parseBooleanLike(getFirstValueByAliases(values, ['enviogratisamba', 'gratisamba', 'amba'])),
                interior: parseBooleanLike(getFirstValueByAliases(values, ['enviogratisinterior', 'gratisinterior', 'interiorgratis', 'interior'])),
                patagonia: parseBooleanLike(getFirstValueByAliases(values, ['enviogratispatagonia', 'gratispatagonia', 'patagoniagratis', 'patagonia']))
            };
            row.dimensiones = {
                alto: normalizeImportedNumericValue(getFirstValueByAliases(values, ['alto'], row.dimensiones.alto), row.dimensiones.alto),
                ancho: normalizeImportedNumericValue(getFirstValueByAliases(values, ['ancho'], row.dimensiones.ancho), row.dimensiones.ancho),
                profundidad: normalizeImportedNumericValue(getFirstValueByAliases(values, ['profundidad', 'prof'], row.dimensiones.profundidad), row.dimensiones.profundidad),
                peso: normalizeImportedNumericValue(getFirstValueByAliases(values, ['peso'], row.dimensiones.peso), row.dimensiones.peso)
            };
        } else {
            row.skuSeller = values[0] ?? '';
            row.titulo = values[1] ?? '';
            row.ean = values[2] ?? '';
            row.descripcionAmpliada = values[3] ?? '';
            row.precioLista = normalizeImportedNumericValue(values[4] ?? '');
            row.precioPromocional = normalizeImportedNumericValue(values[5] ?? '');
            row.stock = normalizeImportedNumericValue(values[6] ?? '0', '0');
            row.codigoLegibilidad = values[7] ?? '';
            row.linkYoutube = values[8] ?? '';
            row.imagenesTexto = values.slice(9, 14).filter((item) => item !== '').slice(0, 5).join('\n');
            row.costosFee = {
                amba: normalizeImportedNumericValue(values[14] ?? ''),
                interior: normalizeImportedNumericValue(values[15] ?? ''),
                patagonia: normalizeImportedNumericValue(values[16] ?? '')
            };
            row.zonasEnvioGratis = {
                amba: parseBooleanLike(values[17]),
                interior: parseBooleanLike(values[18]),
                patagonia: parseBooleanLike(values[19])
            };
            row.dimensiones = {
                alto: normalizeImportedNumericValue(values[20] ?? row.dimensiones.alto, row.dimensiones.alto),
                ancho: normalizeImportedNumericValue(values[21] ?? row.dimensiones.ancho, row.dimensiones.ancho),
                profundidad: normalizeImportedNumericValue(values[22] ?? row.dimensiones.profundidad, row.dimensiones.profundidad),
                peso: normalizeImportedNumericValue(values[23] ?? row.dimensiones.peso, row.dimensiones.peso)
            };
        }

        importedRows.push(row);
    });

    const defaults = createInitialDefaults(isPrivilegedUser ? '' : currentSellerId);
    const importedSellerId = getFirstValueFromRecords(['idseller']);
    const importedCategoria = getFirstValueFromRecords(['idcategoria', 'categoria']);
    const importedMarca = getFirstValueFromRecords(['idmarca', 'marca']);
    const importedTipoPublicacion = getFirstValueFromRecords(['idtipopublicacion', 'tipopublicacion']);
    const importedTipoEntrega = getFirstValueFromRecords(['idtipoentrega', 'tipoentrega']);
    const importedAlicuotaIva = getFirstValueFromRecords(['alicuotaiva', 'idalicuotaiva']);
    const importedImpuestoInterno = getFirstValueFromRecords(['impuestointerno', 'alicuotaimpuestointerno']);
    const importedEnvioGratis = getFirstValueFromRecords(['enviogratis']);
    const importedDescripcion = getFirstValueFromRecords(['descripcionampliada', 'descripcionlarga', 'descripcion']);
    const hasRowDimensions = hasAnyHeader(['alto', 'ancho', 'profundidad', 'prof', 'peso']);

    if (importedCategoria !== '') defaults.categoria = importedCategoria;
    if (importedMarca !== '') defaults.marca = importedMarca;
    if (importedTipoPublicacion !== '') defaults.idTipoPublicacion = importedTipoPublicacion;
    if (importedTipoEntrega !== '') defaults.tipoEntrega = importedTipoEntrega;
    if (importedAlicuotaIva !== '') defaults.alicuotaIva = importedAlicuotaIva;
    if (importedImpuestoInterno !== '') defaults.alicuotaImpuestoInterno = importedImpuestoInterno;
    if (importedEnvioGratis !== '') defaults.envioGratis = parseBooleanLike(importedEnvioGratis);
    if (importedDescripcion !== '' && importedRows.every((row) => row.descripcionAmpliada === importedDescripcion || row.descripcionAmpliada === '')) {
        defaults.descripcionAmpliada = importedDescripcion;
    }
    if (hasRowDimensions) defaults.replicateDimensions = false;

    let selectedSellerId = isPrivilegedUser ? '' : String(currentSellerId ?? '');
    if (importedSellerId !== '') {
        if (isPrivilegedUser) {
            selectedSellerId = importedSellerId;
        } else {
            selectedSellerId = String(currentSellerId ?? '');
        }
    }

    const masivaBulks = importedRows.map((row) => buildMasivaBulkItem(row, defaults, selectedSellerId));

    return {
        masivaBulks,
        idUser: userId ?? 0
    };
};

export const buildMasivaBulkRequestFromCsvText = (rawText, options = {}) => {
    const text = String(rawText ?? '').trim();
    if (text === '') {
        return { masivaBulks: [], idUser: options.userId ?? 0 };
    }

    const delimiter = detectCreacionMasivaDelimiter(text);
    const records = parseCreacionMasivaDelimitedText(text, delimiter);
    return buildMasivaBulkRequestFromRecords(records, options);
};

export const extractCreacionMasivaBulkResult = (response) => {
    const data = response?.data ?? response ?? {};

    const created = Array.isArray(response?.publicacion)
        ? response.publicacion
        : Array.isArray(response?.publicaciones)
            ? response.publicaciones
            : Array.isArray(data?.publicacion)
                ? data.publicacion
                : Array.isArray(data?.publicaciones)
                    ? data.publicaciones
                    : Array.isArray(data?.Publicacion)
                        ? data.Publicacion
                        : Array.isArray(data?.Publicaciones)
                            ? data.Publicaciones
                            : [];

    const errors = Array.isArray(response?.errors)
        ? response.errors
        : Array.isArray(response?.errores)
            ? response.errores
            : Array.isArray(data?.errors)
                ? data.errors
                : Array.isArray(data?.errores)
                    ? data.errores
                    : Array.isArray(data?.Errors)
                        ? data.Errors
                        : Array.isArray(data?.Errores)
                            ? data.Errores
                            : [];

    return { created, errors };
};

export const formatCreacionMasivaBulkError = (item) => {
    if (item == null) return 'Sin detalle';
    if (typeof item === 'string') return item;

    const sku = item.skuSeller ?? item.SkuSeller ?? '';
    const message = item.errorMesage ?? item.ErrorMesage ?? item.message ?? item.Message ?? JSON.stringify(item);
    return sku ? `${sku}: ${message}` : message;
};
